import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Landing page after admin login. Two destinations: the editor and a
// non-technical guide. Kept deliberately small — it's a chooser, not a
// dashboard — and the visual language matches AdminEdit so the section
// transitions feel like one app.

export default function AdminHome() {
  const navigate = useNavigate();
  const [views, setViews] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    document.title = "Admin · Ruby Fos";
  }, []);

  useEffect(() => {
    fetch("/api/admin/views", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setViews(data.views); })
      .catch(() => {});
  }, []);

  async function resetViews() {
    if (!window.confirm("Reset view count to zero?")) return;
    setResetting(true);
    try {
      const r = await fetch("/api/admin/views", {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (r.ok) setViews(0);
    } catch {}
    setResetting(false);
  }

  async function logout() {
    await fetch("/api/admin/logout", {
      method: "POST",
      credentials: "same-origin",
    });
    navigate("/admin/login", { replace: true });
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0c0c0c",
        color: "#f4ece0",
        fontFamily: "system-ui, -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          padding: "1.2rem 1.5rem",
          borderBottom: "1px solid #1a1a1a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: "1.05rem", letterSpacing: "0.04em" }}>
          Ruby Fos · admin
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Link
            to="/"
            target="_blank"
            rel="noopener"
            style={{
              color: "#888",
              textDecoration: "none",
              fontSize: "0.85rem",
            }}
          >
            view site ↗
          </Link>
          <button
            type="button"
            onClick={logout}
            style={{
              background: "transparent",
              color: "#aaa",
              border: "none",
              padding: "0.3rem 0.5rem",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            sign out
          </button>
        </div>
      </header>

      <main
        style={{
          flex: 1,
          display: "grid",
          placeItems: "center",
          padding: "2rem 1.5rem",
        }}
      >
        <div style={{ maxWidth: 720, width: "100%" }}>
          <h1
            style={{
              fontSize: "1.6rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              margin: 0,
              marginBottom: "1rem",
            }}
          >
            Welcome.
          </h1>
          <p
            style={{
              color: "#cfcabd",
              fontSize: "1rem",
              lineHeight: 1.6,
              marginTop: 0,
              marginBottom: "2.4rem",
            }}
          >
            This is the private side of <strong>rubys-website.pages.dev</strong>.
            From here you can change anything visitors see — text, photos, the
            reel video, contact details — without writing any code or asking
            anyone to redeploy. Pick where you want to go.
          </p>

          <div
            style={{
              marginBottom: "2rem",
              padding: "1rem 1.4rem",
              background: "#0f0f0f",
              border: "1px solid #1a1a1a",
              borderRadius: 6,
              display: "inline-flex",
              flexDirection: "column",
              gap: "0.25rem",
            }}
          >
            <div style={{ fontSize: "0.78rem", color: "#666", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              total page views
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ fontSize: "2rem", fontWeight: 500, color: "#f4ece0", letterSpacing: "0.02em" }}>
                {views === null ? "—" : views.toLocaleString()}
              </div>
              <button
                type="button"
                onClick={resetViews}
                disabled={resetting || views === null}
                style={{
                  background: "transparent",
                  border: "1px solid #333",
                  color: "#666",
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.6rem",
                  borderRadius: 4,
                  cursor: "pointer",
                  letterSpacing: "0.04em",
                }}
              >
                {resetting ? "resetting…" : "reset"}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "1rem",
            }}
          >
            <Link to="/admin/edit" style={cardStyle}>
              <div style={cardTitleStyle}>Edit the site</div>
              <div style={cardBodyStyle}>
                Open the editor. Change text, swap photos, replace the reel
                video, save when ready. Saves go live within a minute.
              </div>
              <div style={cardCtaStyle}>open editor →</div>
            </Link>

            <Link to="/admin/guide" style={cardStyle}>
              <div style={cardTitleStyle}>How this works</div>
              <div style={cardBodyStyle}>
                A plain-language guide to the site, the editor, what's allowed,
                and what to do when something looks wrong. No coding knowledge
                assumed.
              </div>
              <div style={cardCtaStyle}>read the guide →</div>
            </Link>
          </div>

          <div
            style={{
              marginTop: "2rem",
              fontSize: "0.85rem",
              color: "#666",
              display: "flex",
              gap: "1.2rem",
            }}
          >
            <Link
              to="/admin/revisions"
              style={{ color: "#888", textDecoration: "none" }}
            >
              past versions / restore
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.6rem",
  padding: "1.4rem 1.4rem 1.2rem",
  background: "#0f0f0f",
  border: "1px solid #1a1a1a",
  borderRadius: 6,
  textDecoration: "none",
  color: "#f4ece0",
  transition: "border-color 120ms, transform 120ms",
  cursor: "pointer",
};

const cardTitleStyle = {
  fontSize: "1.05rem",
  fontWeight: 500,
  letterSpacing: "0.04em",
};

const cardBodyStyle = {
  color: "#aaa",
  fontSize: "0.88rem",
  lineHeight: 1.5,
};

const cardCtaStyle = {
  marginTop: "0.4rem",
  color: "#f4ece0",
  fontSize: "0.85rem",
  letterSpacing: "0.04em",
};
